/**
 * ProjectsPanel — připojené GitLab projekty per tenant.
 *
 * Widget-first (viz `widgets/registry.gitlab.tsx`): custom widget content, žádný
 * vnější Card ani hlavička — chrome kreslí WidgetShell.
 *
 * Zdroj:
 *   - `gitlab_projects_list` — projekty ve scope (tenant filter)
 *   - `gitlab_project_connect` — připojit projekt do registru
 *   - členové a access tokeny patří k projektu, ne visí samostatně
 *
 * URL link se rekonstruuje z `connection.host + path` (root of truth =
 * registrovaná connection, ne self-report z GitLab API).
 */
import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, ExternalLink, GitBranch, Link2, Server, Users } from 'lucide-react';
import { api } from '../../../lib/api';
import { Badge, Button, EmptyState, Input } from '../../../components/ui';
import { Select } from '../../../components/ui/Select';
import type { WidgetRenderProps } from '../../../widgets/types';
import { ProjectRequests } from './ProjectRequests';
import { MembersBlock } from './MembersBlock';
import { TokensBlock } from './TokensBlock';

interface Project {
  connection_id?: string;
  connection_host?: string | null;
  project_id?: number;
  path?: string;
  name?: string;
  web_url?: string;
  default_branch?: string | null;
  tenant_id?: string | null;
  node?: string | null;
}
interface ListResp {
  ok: boolean;
  total: number;
  projects: Project[];
}
interface ServersResp {
  ok: boolean;
  total: number;
  servers: Array<{
    id: string;
    host: string;
    has_token: boolean;
    node?: string | null;
    instance_name?: string | null;
    kind?: 'acm' | 'external';
  }>;
}

export function ProjectsPanel({ scope }: WidgetRenderProps = {} as WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const sc = scope ?? { node: null, identity: null };
  const [project, setProject] = useState('');
  const [assignTenant, setAssignTenant] = useState('');
  const [selectedServer, setSelectedServer] = useState('');
  const [openPath, setOpenPath] = useState<string | null>(null);

  const q = useQuery<ListResp>({
    queryKey: ['gitlab-projects'],
    queryFn: () => api.mcp<ListResp>('gitlab.projects_list', {}, { silent: true }),
    staleTime: 30_000,
  });

  const serversQ = useQuery<ServersResp>({
    queryKey: ['gitlab-connections'],
    queryFn: () => api.mcp('gitlab.connections_list', {}, { silent: true }),
    staleTime: 30_000,
  });

  const connectMut = useMutation({
    mutationFn: () =>
      api.mcp('gitlab.project_connect', {
        project,
        ...(assignTenant ? { tenant_id: assignTenant } : {}),
        ...(selectedServer ? { connection_id: selectedServer } : {}),
      }),
    onSuccess: (r: unknown) => {
      const res = r as { ok?: boolean; error?: string; project?: string };
      if (res?.ok === false) {
        toast.error(res.error ?? t('gitlab.toastConnectFailed'));
        return;
      }
      toast.success(t('gitlab.toastConnected', { name: res.project ?? project }));
      setProject('');
      setAssignTenant('');
      qc.invalidateQueries({ queryKey: ['gitlab-projects'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const projects = q.data?.projects ?? [];
  const servers = serversQ.data?.servers ?? [];

  const serverById = new Map(servers.map((s) => [s.id, s]));
  function projectHref(p: Project): string | null {
    const host = p.connection_host
      ?? (p.connection_id ? serverById.get(p.connection_id)?.host : undefined);
    if (host && p.path) return `${host.replace(/\/+$/, '')}/${p.path}`;
    return p.web_url ?? null;
  }
  function gitlabLabel(p: Project): { id: string; host: string | null; node: string | null } {
    const id = p.connection_id ?? '—';
    const srv = p.connection_id ? serverById.get(p.connection_id) : undefined;
    return {
      id,
      host: p.connection_host ?? srv?.host ?? null,
      node: p.node ?? srv?.node ?? null,
    };
  }

  return (
    <div className="p-3 space-y-3">
      <div className="flex flex-col sm:flex-row gap-2">
        {servers.length > 0 && (
          <Select
            value={selectedServer}
            onChange={(e) => setSelectedServer(e.target.value)}
            title="GitLab server"
          >
            <option value="">
              {servers.length === 1 ? servers[0]?.id : t('gitlab.serverAuto')}
            </option>
            {servers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.id} ({s.host}){s.node ? ` @ ${s.node}` : ''}
              </option>
            ))}
          </Select>
        )}
        <Input
          placeholder={t('gitlab.projectPlaceholder')}
          value={project}
          onChange={(e) => setProject(e.target.value)}
          className="flex-1"
        />
        <Input
          placeholder={t('gitlab.tenantIdPlaceholder')}
          value={assignTenant}
          onChange={(e) => setAssignTenant(e.target.value)}
          className="flex-1"
        />
        <Button
          variant="primary"
          size="sm"
          loading={connectMut.isPending}
          disabled={!project}
          onClick={() => connectMut.mutate()}
        >
          <Link2 size={13} /> {t('gitlab.connectBtn')}
        </Button>
      </div>

      {q.isLoading
        ? <div className="text-sm text-zinc-500">{t('common.loading')}</div>
        : projects.length === 0
        ? (
          <EmptyState
            title={t('gitlab.noProjectsTitle')}
            description={t('gitlab.noProjectsDesc')}
          />
        )
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-zinc-800 text-xs text-zinc-400">
                  <th className="text-left py-2 pr-3">{t('gitlab.colProject')}</th>
                  <th className="text-left py-2 pr-3">{t('gitlab.colGitlab', 'GitLab')}</th>
                  <th className="text-left py-2 pr-3">{t('gitlab.colBranch')}</th>
                  <th className="text-left py-2 pr-3">{t('gitlab.colTenant')}</th>
                  <th className="text-right py-2 pr-3">{t('gitlab.colActions', 'Akce')}</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => {
                  const gl = gitlabLabel(p);
                  const path = p.path ?? '';
                  const open = !!path && openPath === path;
                  return (
                  <Fragment key={`${p.connection_id}:${p.project_id}`}>
                  <tr
                    className="border-b border-zinc-900 hover:bg-zinc-900/30"
                  >
                    <td className="py-2 pr-3">
                      <div className="font-medium">{p.name ?? p.path}</div>
                      <div className="text-xs text-zinc-500">{p.path}</div>
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="inline-flex items-center gap-1 text-xs text-zinc-200">
                          <GitBranch size={11} className="text-zinc-500 shrink-0" />
                          <span className="font-mono truncate">{gl.id}</span>
                        </span>
                        {gl.host && (
                          <span className="text-[10px] text-zinc-500 truncate max-w-[14rem]">
                            {gl.host.replace(/^https?:\/\//, '')}
                          </span>
                        )}
                        {gl.node && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-violet-300/90">
                            <Server size={10} />
                            {gl.node}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-xs text-zinc-400">
                      {p.default_branch ?? '—'}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge>
                        {p.tenant_id ? p.tenant_id.slice(0, 8) : t('gitlab.rootOrg')}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center justify-end gap-2">
                        {path && (
                          <Button
                            variant={open ? 'secondary' : 'ghost'}
                            size="sm"
                            onClick={() => setOpenPath(open ? null : path)}
                          >
                            {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                            <Users size={13} />
                            {t('gitlab.members.open')}
                          </Button>
                        )}
                        {(() => {
                          const href = projectHref(p);
                          if (!href) return null;
                          return (
                            <a
                              href={href}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sky-400 hover:text-sky-300 inline-flex items-center gap-1 text-xs"
                            >
                              GitLab <ExternalLink size={12} />
                            </a>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={5} className="pb-3 pt-0 space-y-2">
                        <ProjectRequests connection={p.connection_id} project={String(p.project_id ?? path)} scope={sc} />
                        <MembersBlock
                          scope={sc}
                          connectionId={p.connection_id}
                          target="project"
                          targetRef={path}
                        />
                        {typeof p.project_id === 'number' && p.project_id > 0 && (
                          <TokensBlock
                            scope={sc}
                            connectionId={p.connection_id}
                            target="project"
                            targetId={p.project_id}
                          />
                        )}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
